import { useState } from "react";
import { Alert, Button, Checkbox, Form, Input, message, Modal, Segmented, Select } from "antd";
import TextArea from "antd/lib/input/TextArea";
import { UserAddOutlined } from "@ant-design/icons";
import { formatDateRangeWithDateString } from "../../Helpers/DateHelpers";
import { getCurrentLanguage } from "../../Helpers/RouteHelper";
import { WebApiMemberDtoMemberDto } from "../../Repository/eventbookyapi";
import { MemberService } from "../../Services/MemberService";
import { ReplyService } from "../../Services/ReplyService";
import { TextService } from "../../Services/TextService";
import { EventType } from "../../Types/EventType";

/**
 * Lets an admin add a participant to the event, for example someone who calls in: a member from the
 * register, or another person that is not a member (only for events that are open for all).
 */
export const AddMemberReplyModal = (props: any) => {
    const GetText = TextService.GetText;
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [members, setMembers] = useState([] as WebApiMemberDtoMemberDto[]);
    const [memberId, setMemberId] = useState(undefined as number | undefined);
    const [timeIds, setTimeIds] = useState([] as number[]);
    const [note, setNote] = useState("");
    const [sendConfirmation, setSendConfirmation] = useState(true);
    // "member" adds a member from the register, "guest" another person that is not a member.
    const [mode, setMode] = useState("member" as "member" | "guest");
    const [guestName, setGuestName] = useState("");
    const [guestEmail, setGuestEmail] = useState("");
    const [guestPhone, setGuestPhone] = useState("");
    const isGuest = mode === "guest";
    const isMembersOnly = props.event.membersOnly === true;

    const times: any[] = props.event.times ?? [];
    const selectedMember = members.find(member => member.memberId === memberId);
    const hasEmail = !!selectedMember?.email;
    // A family member without an own e-mail gets the confirmation through the primary member.
    const primaryMember = selectedMember?.primaryMemberId != null
        ? members.find(member => member.memberId === selectedMember.primaryMemberId)
        : undefined;
    const confirmationToPrimary = !hasEmail && !!primaryMember?.email;
    const canConfirm = isGuest ? guestEmail.trim() !== "" : hasEmail || confirmationToPrimary;

    const onOpen = async () => {
        setMemberId(undefined);
        setMode("member");
        setGuestName("");
        setGuestEmail("");
        setGuestPhone("");
        setNote("");
        setSendConfirmation(true);
        // With one time there is nothing to choose.
        setTimeIds(times.length === 1 ? [times[0].id] : []);
        setOpen(true);

        const result = await MemberService.GetAllMembers();
        setMembers(result.members ?? []);
    };

    // A member that already has a reply can't be added again. Replies the member made without logging in
    // are found by e-mail, but not for a family member, since the family often shares the e-mail.
    const hasReplied = (member: WebApiMemberDtoMemberDto) => {
        return props.event.timeAnswers?.some((reply: any) => reply.memberId === member.memberId
            || (!!member.email && member.primaryMemberId == null && reply.memberId == null
                && reply.customerEmail?.toLowerCase() === member.email.toLowerCase())) === true;
    };

    const getMemberLabel = (member: WebApiMemberDtoMemberDto) => {
        const name = ((member.firstName ?? "") + " " + (member.lastName ?? "")).trim();
        const label = member.email ? name + " (" + member.email + ")" : name;
        return hasReplied(member) ? label + " – " + GetText("AddMemberReply_AlreadyReplied") : label;
    };

    const isFullyBooked = (time: any) => {
        // A poll takes every vote.
        if (props.event.eventType === EventType.Poll) {
            return false;
        }

        return (time.yesCount ?? 0) >= (props.event.eventParticipants ?? 0)
            || (props.event.allowWaitlist === true && (time.waitlistCount ?? 0) > 0);
    };

    const getTimeLabel = (time: any) => {
        const label = formatDateRangeWithDateString(time.start, time.end, time.isFullDay, getCurrentLanguage() === "se");

        if (!isFullyBooked(time)) {
            return label;
        }

        return label + " – " + GetText(props.event.allowWaitlist ? "AddMemberReply_FullGoesToWaitlist" : "Event_TimeFullyBooked");
    };

    const onSave = async () => {
        if ((isGuest ? guestName.trim() === "" : memberId === undefined) || timeIds.length === 0) {
            message.warning(GetText(isGuest ? "AddMemberReply_EnterNameAndTime" : "AddMemberReply_SelectMemberAndTime"));
            return;
        }

        setSaving(true);

        try {
            const result = isGuest
                ? await ReplyService.AddGuest({
                    eventId: props.event.eventId,
                    name: guestName,
                    email: guestEmail,
                    phone: guestPhone,
                    timeIds: timeIds,
                    note: note,
                    sendConfirmation: sendConfirmation && canConfirm,
                })
                : await ReplyService.AddMember({
                    eventId: props.event.eventId,
                    memberId: memberId!,
                    timeIds: timeIds,
                    note: note,
                    sendConfirmation: sendConfirmation && canConfirm,
                });

            const isWaitlisted = (result.waitlistedTimeIds?.length ?? 0) > 0;
            const hasPlace = (result.acceptedTimeIds?.length ?? 0) > 0;
            message.success(GetText(!isWaitlisted ? "AddMemberReply_Added" : hasPlace ? "AddMemberReply_AddedPartly" : "AddMemberReply_AddedToWaitlist"));
            setOpen(false);
            props.onAdded();
        } catch (error: any) {
            const status = error?.response?.status ?? error?.status;
            message.error(GetText(status === 409 ? "AddMemberReply_FullyBooked" : isGuest && status === 400 ? "AddMemberReply_GuestNotAdded" : "Common_TempError"));
        }

        setSaving(false);
    };

    return <>
        <Button icon={<UserAddOutlined />} onClick={onOpen}>{GetText("AddMemberReply_Button")}</Button>
        <Modal
            open={open}
            title={GetText("AddMemberReply_Title")}
            okText={GetText("AddMemberReply_Save")}
            cancelText={GetText("Common_Cancel")}
            confirmLoading={saving}
            onOk={onSave}
            onCancel={() => setOpen(false)}
        >
            <Form layout="vertical">
                <Form.Item>
                    <Segmented
                        data-testid="add-reply-mode"
                        value={mode}
                        onChange={(value) => setMode(value as "member" | "guest")}
                        options={[
                            { label: GetText("AddMemberReply_ModeMember"), value: "member" },
                            { label: GetText("AddMemberReply_ModeGuest"), value: "guest", disabled: isMembersOnly },
                        ]}
                    />
                    {isMembersOnly ? <div style={{ fontSize: "80%", color: "#888", marginTop: 4 }}>{GetText("AddMemberReply_GuestMembersOnly")}</div> : null}
                </Form.Item>

                {isGuest ? <>
                    <Form.Item label={GetText("AddMemberReply_GuestName")} required>
                        <Input data-testid="guest-name" value={guestName} onChange={(e) => setGuestName(e.target.value)} />
                    </Form.Item>
                    <Form.Item label={GetText("AddMemberReply_GuestPhone")}>
                        <Input data-testid="guest-phone" value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} />
                    </Form.Item>
                    <Form.Item label={GetText("AddMemberReply_GuestEmail")} extra={GetText("AddMemberReply_GuestEmailInfo")}>
                        <Input data-testid="guest-email" type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} />
                    </Form.Item>
                </> :
                <Form.Item label={GetText("AddMemberReply_Member")} required>
                    <Select
                        showSearch
                        placeholder={GetText("AddMemberReply_MemberPlaceholder")}
                        optionFilterProp="label"
                        value={memberId}
                        onChange={(value) => setMemberId(value)}
                        options={members.map(member => ({
                            value: member.memberId,
                            label: getMemberLabel(member),
                            disabled: hasReplied(member),
                        }))}
                    />
                </Form.Item>}

                <Form.Item label={GetText("AddMemberReply_Time")} required>
                    <Checkbox.Group value={timeIds} onChange={(values) => setTimeIds(values as number[])}>
                        {times.map(time =>
                            <div key={time.id} style={{ width: "100%" }}>
                                <Checkbox value={time.id} disabled={isFullyBooked(time) && !props.event.allowWaitlist}>{getTimeLabel(time)}</Checkbox>
                            </div>
                        )}
                    </Checkbox.Group>
                </Form.Item>

                <Form.Item label={<span>{GetText("AddMemberReply_Note")} <span style={{ fontSize: "80%", color: "#888" }}>{GetText("AddMemberReply_NoteInfo")}</span></span>}>
                    <TextArea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
                </Form.Item>

                <Checkbox checked={sendConfirmation && canConfirm} disabled={!canConfirm} onChange={(e) => setSendConfirmation(e.target.checked)}>
                    {GetText("AddMemberReply_SendConfirmation")}
                </Checkbox>
                {isGuest && !canConfirm ? <div><i>{GetText("AddMemberReply_GuestNoEmail")}</i></div> : null}
                {!isGuest && confirmationToPrimary ? <div><i>{GetText("AddMemberReply_ConfirmationToPrimary")} {((primaryMember?.firstName ?? "") + " " + (primaryMember?.lastName ?? "")).trim()} ({primaryMember?.email}).</i></div> : null}
                {!isGuest && selectedMember && !canConfirm ? <div><i>{GetText("AddMemberReply_NoEmail")}</i></div> : null}
            </Form>
        </Modal>
    </>;
};
